import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApplicationIcon,
  Box,
  Icon,
  SidePanel,
  Typography,
} from '@saviynt/design-system';
import { FormattedMessage } from 'react-intl';

import { TOKENS } from '../../constants';
import { type AgentRow } from '../ManagePageTable';

import SidePanelLabelsTab from './SidePanelLabelsTab';
import SidePanelOverviewTab from './SidePanelOverviewTab';
import SidePanelOwnersTab from './SidePanelOwnersTab';
import SidePanelTabs from './SidePanelTabs';

import './ManagePageSidePanel.css';

interface ManagePageSidePanelProps {
  agent?: AgentRow | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Side panel for viewing and editing a selected agent's details.
 *
 * Slides in from the right when an agent row is selected. Renders a
 * {@link SidePanel} with the agent name and AI-agent icon in the header,
 * and a three-tab layout managed by {@link SidePanelTabs}:
 * - Overview (tab `"1"`) — read-only agent properties with inline criticality editing.
 * - Labels (tab `"2"`) — sortable label table with an edit modal.
 * - Owners (tab `"3"`) — sortable owner table with an edit modal.
 *
 * The active tab resets to Overview whenever `agent.agentId` changes.
 * If `agent` is `null`, the panel renders nothing; if the agent object is
 * missing after mount, an error message is shown instead.
 *
 * @param props.agent - The {@link AgentRow} to display, or `null`/`undefined`.
 * @param props.isOpen - Controls panel visibility.
 * @param props.onClose - Called when the user requests the panel to close.
 *
 * @returns The side panel with tabbed agent details, or `null` when no agent is provided.
 */
export default function ManagePageSidePanel({
  agent,
  isOpen,
  onClose,
}: ManagePageSidePanelProps) {
  const [activeTab, setActiveTab] = useState('1');
  const sidepanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveTab('1');
  }, [agent?.agentId]);

  const handleChange = useCallback((value: string) => {
    setActiveTab(value);
  }, []);

  const renderLayout = useCallback(() => {
    if (!agent) {
      return (
        <div className='ManagePageSidePanel-loadingWrapper w-full h-full flex items-center justify-center flex-col flex-nowrap gap-6'>
          <Box
            tag='div'
            className='ManagePageSidePanel-loadingWrapperMessage flex items-center justify-center flex-col flex-nowrap gap-1 mx-20'>
            <Icon kind='StopProcess' size='large' color={TOKENS.CF_CRT_MED} />
            <Typography kind='body2' color={TOKENS.CF_CRT_MED}>
              <FormattedMessage
                id='ghxhgY'
                defaultMessage="Data couldn't be retrieved."
                description="Data couldn't be retrieved."
              />
            </Typography>
          </Box>
        </div>
      );
    }

    return (
      <div className='ManagePageSidePanel-layoutWrapper w-full h-full overflow-hidden flex flex-col justify-start'>
        <SidePanelTabs
          sidepanelRef={sidepanelRef}
          handleChange={handleChange}
          activeTab={activeTab}
          agent={agent}
        />
        <SidePanelOverviewTab activeTab={activeTab} agent={agent} />
        <SidePanelLabelsTab
          activeTab={activeTab}
          labelDetails={{
            agentId: agent.agentId,
            labels: agent.labels ?? {},
          }}
        />
        <SidePanelOwnersTab
          activeTab={activeTab}
          ownersDetails={{
            agentId: agent.agentId,
            owners: agent.owners,
          }}
          agentMetadata={agent.agentMetadata}
        />
      </div>
    );
  }, [agent, activeTab]);

  return (
    <SidePanel
      ref={sidepanelRef}
      header={{
        title: agent?.name,
        headerIcon: (
          <ApplicationIcon
            icon='AiAgent'
            iconColor={TOKENS.CF_SAV_MED}
            size='large'
          />
        ),
      }}
      layout={renderLayout()}
      isOpen={isOpen}
      onClose={onClose}
      isSidePanelNavigatingList
      className='ManagePageSidePanel'
      classNames={{
        headerClassName: 'ManagePageSidePanel-header',
        layoutClassName: 'ManagePageSidePanel-layout',
        innerContentClassName: 'ManagePageSidePanel-innerContent',
        footerClassName: 'ManagePageSidePanel-footer',
      }}
    />
  );
}
